import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { EmojiReactionPayload } from '@deadpan/shared';
import { useSocket } from '../contexts/SocketContext';
import { useTranslations } from '../hooks/useTranslations';

const FLOAT_MARGIN = 8;
const COLLAPSED_SIZE = 56;
const EXPANDED_WIDTH = 260;
const BUBBLE_MIN_MS = 1700;
const BUBBLE_MAX_MS = 2400;
const BUBBLE_CLEANUP_INTERVAL_MS = 500;
const BUBBLE_TTL_BUFFER_MS = 250;
/** Exactly 25 emojis in a 5×5 grid when the panel is open. */
const REACTION_GRID_EMOJIS: readonly EmojiReactionPayload['emoji'][] = [
  '👍',
  '😂',
  '😱',
  '😊',
  '🎉',
  '😁',
  '😅',
  '😮',
  '😉',
  '❤️',
  '🔥',
  '👏',
  '🙏',
  '🥳',
  '🤝',
  '💯',
  '👌',
  '😎',
  '🤯',
  '🥲',
  '😭',
  '🤔',
  '🙌',
  '✅',
  '🤣',
];

type SnapSide = 'left' | 'right';
type ReactionEmoji = EmojiReactionPayload['emoji'];

type ReactionBubble = {
  id: string;
  emoji: ReactionEmoji;
  leftPercent: number;
  sizePx: number;
  durationMs: number;
  createdAt: number;
};

function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export const EmojiReactionSection: React.FC = () => {
  const { socket, currentRoom } = useSocket();
  const t = useTranslations();
  const [expanded, setExpanded] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [snapSide, setSnapSide] = useState<SnapSide>('right');
  const positionRef = useRef<{ x: number; y: number } | null>(null);
  const dragOffsetRef = useRef<{ x: number; y: number } | null>(null);
  const draggingRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const movedDuringDragRef = useRef(false);
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const bubbleIdRef = useRef(0);
  const [bubbles, setBubbles] = useState<ReactionBubble[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const highlightClearTimerRef = useRef<number | null>(null);

  const emitReaction = useCallback(
    (emoji: ReactionEmoji) => {
      if (!socket || !currentRoom) return;
      socket.emit('client:send-emoji-reaction', {
        roomCode: currentRoom.code,
        emoji,
      });
    },
    [currentRoom, socket],
  );

  const clearHighlightTimer = useCallback(() => {
    if (highlightClearTimerRef.current !== null) {
      window.clearTimeout(highlightClearTimerRef.current);
      highlightClearTimerRef.current = null;
    }
  }, []);

  const handleGridEmojiClick = useCallback(
    (emoji: ReactionEmoji, index: number) => {
      clearHighlightTimer();
      setHighlightedIndex(index);
      emitReaction(emoji);
      highlightClearTimerRef.current = window.setTimeout(() => {
        setHighlightedIndex(null);
        highlightClearTimerRef.current = null;
      }, 320);
    },
    [clearHighlightTimer, emitReaction],
  );

  const clampToViewport = useCallback((x: number, y: number, width: number, height: number) => {
    const clampedX = Math.min(
      Math.max(FLOAT_MARGIN, x),
      Math.max(FLOAT_MARGIN, window.innerWidth - width - FLOAT_MARGIN),
    );
    const clampedY = Math.min(
      Math.max(FLOAT_MARGIN, y),
      Math.max(FLOAT_MARGIN, window.innerHeight - height - FLOAT_MARGIN),
    );
    return { x: clampedX, y: clampedY };
  }, []);

  const snapToSide = useCallback(
    (x: number, y: number, width: number, height: number, preferredSide?: SnapSide) => {
      const clamped = clampToViewport(x, y, width, height);
      const leftDist = Math.abs(clamped.x - FLOAT_MARGIN);
      const rightX = Math.max(FLOAT_MARGIN, window.innerWidth - width - FLOAT_MARGIN);
      const rightDist = Math.abs(clamped.x - rightX);
      const side: SnapSide = preferredSide ?? (leftDist <= rightDist ? 'left' : 'right');
      const snapX = side === 'left' ? FLOAT_MARGIN : rightX;
      return { position: { x: snapX, y: clamped.y }, side };
    },
    [clampToViewport],
  );

  const detectCurrentSide = useCallback((): SnapSide => {
    const panel = panelRef.current;
    if (panel && positionRef.current) {
      const width = panel.getBoundingClientRect().width || panel.offsetWidth || COLLAPSED_SIZE;
      const rightX = Math.max(FLOAT_MARGIN, window.innerWidth - width - FLOAT_MARGIN);
      const leftDist = Math.abs(positionRef.current.x - FLOAT_MARGIN);
      const rightDist = Math.abs(positionRef.current.x - rightX);
      return leftDist <= rightDist ? 'left' : 'right';
    }
    return 'right';
  }, []);

  const spawnBubbles = useCallback((emoji: ReactionEmoji) => {
    const count = randomInt(5, 8);
    const now = Date.now();
    const next: ReactionBubble[] = [];
    for (let i = 0; i < count; i += 1) {
      bubbleIdRef.current += 1;
      next.push({
        id: `${now}-${bubbleIdRef.current}`,
        emoji,
        leftPercent: randomInt(12, 88),
        sizePx: randomInt(28, 44),
        durationMs: randomInt(BUBBLE_MIN_MS, BUBBLE_MAX_MS),
        createdAt: now + randomInt(0, 180),
      });
    }
    setBubbles((prev) => [...prev, ...next]);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      const now = Date.now();
      setBubbles((prev) =>
        prev.filter((bubble) => now - bubble.createdAt < bubble.durationMs + BUBBLE_TTL_BUFFER_MS),
      );
    }, BUBBLE_CLEANUP_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!socket || !currentRoom) return;
    const onReaction = (payload: { emoji: ReactionEmoji }): void => {
      spawnBubbles(payload.emoji);
    };
    socket.on('server:emoji-reaction', onReaction);
    return () => {
      socket.off('server:emoji-reaction', onReaction);
    };
  }, [currentRoom, socket, spawnBubbles]);

  useEffect(() => {
    if (!currentRoom) return;
    const initial = {
      x: Math.max(FLOAT_MARGIN, window.innerWidth - COLLAPSED_SIZE - FLOAT_MARGIN),
      y: Math.max(FLOAT_MARGIN, window.innerHeight - COLLAPSED_SIZE - FLOAT_MARGIN),
    };
    setPosition(initial);
    setSnapSide('right');
    positionRef.current = initial;
    setExpanded(false);
    setBubbles([]);
    clearHighlightTimer();
    setHighlightedIndex(null);
  }, [clearHighlightTimer, currentRoom?.code]);

  useEffect(() => {
    return () => {
      clearHighlightTimer();
    };
  }, [clearHighlightTimer]);

  const startDrag = (event: React.MouseEvent<HTMLElement>) => {
    if (event.button !== 0) return;
    const panel = panelRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    dragOffsetRef.current = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
    dragStartRef.current = { x: event.clientX, y: event.clientY };
    draggingRef.current = true;
    movedDuringDragRef.current = false;
    if (!position) {
      const next = { x: rect.left, y: rect.top };
      positionRef.current = next;
      setPosition(next);
    }
  };

  const alignForExpandedState = useCallback(
    (nextExpanded: boolean) => {
      const panel = panelRef.current;
      if (!panel) return;
      const rect = panel.getBoundingClientRect();
      const basePos = positionRef.current ?? { x: rect.left, y: rect.top };
      const width = nextExpanded
        ? Math.min(EXPANDED_WIDTH, Math.max(COLLAPSED_SIZE, window.innerWidth - FLOAT_MARGIN * 2))
        : COLLAPSED_SIZE;
      const height = nextExpanded ? panel.offsetHeight : COLLAPSED_SIZE;
      const side = detectCurrentSide();
      const snapped = snapToSide(basePos.x, basePos.y, width, height, side);
      positionRef.current = snapped.position;
      setPosition(snapped.position);
      setSnapSide(snapped.side);
    },
    [detectCurrentSide, snapToSide],
  );

  const toggleExpanded = (nextExpanded: boolean) => {
    alignForExpandedState(nextExpanded);
    setExpanded(nextExpanded);
  };

  const realignPanelToViewport = useCallback(() => {
    if (!panelRef.current || !positionRef.current) return;
    const width = panelRef.current.getBoundingClientRect().width || panelRef.current.offsetWidth;
    const height = panelRef.current.getBoundingClientRect().height || panelRef.current.offsetHeight;
    const side = detectCurrentSide();
    const snapped = snapToSide(positionRef.current.x, positionRef.current.y, width, height, side);
    positionRef.current = snapped.position;
    setPosition(snapped.position);
    setSnapSide(snapped.side);
  }, [detectCurrentSide, snapToSide]);

  useEffect(() => {
    const onMouseMove = (event: MouseEvent) => {
      if (!draggingRef.current || !dragOffsetRef.current || !panelRef.current) return;
      if (dragStartRef.current) {
        const deltaX = Math.abs(event.clientX - dragStartRef.current.x);
        const deltaY = Math.abs(event.clientY - dragStartRef.current.y);
        if (deltaX > 3 || deltaY > 3) {
          movedDuringDragRef.current = true;
          if (!isDragging) setIsDragging(true);
        }
      }
      const width = panelRef.current.offsetWidth;
      const height = panelRef.current.offsetHeight;
      const nextX = event.clientX - dragOffsetRef.current.x;
      const nextY = event.clientY - dragOffsetRef.current.y;
      const next = clampToViewport(nextX, nextY, width, height);
      positionRef.current = next;
      setPosition(next);
    };

    const onMouseUp = () => {
      if (draggingRef.current && movedDuringDragRef.current && panelRef.current && positionRef.current) {
        const width = panelRef.current.offsetWidth;
        const height = panelRef.current.offsetHeight;
        const snapped = snapToSide(positionRef.current.x, positionRef.current.y, width, height);
        positionRef.current = snapped.position;
        setPosition(snapped.position);
        setSnapSide(snapped.side);
      }
      draggingRef.current = false;
      setIsDragging(false);
      movedDuringDragRef.current = false;
      dragStartRef.current = null;
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [clampToViewport, isDragging, snapToSide]);

  useEffect(() => {
    const onResize = () => {
      if (!panelRef.current || !positionRef.current) return;
      const width = panelRef.current.offsetWidth;
      const height = panelRef.current.offsetHeight;
      const side = detectCurrentSide();
      const snapped = snapToSide(positionRef.current.x, positionRef.current.y, width, height, side);
      positionRef.current = snapped.position;
      setPosition(snapped.position);
      setSnapSide(snapped.side);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [detectCurrentSide, snapToSide]);

  useEffect(() => {
    realignPanelToViewport();
  }, [expanded, realignPanelToViewport]);

  useLayoutEffect(() => {
    if (!expanded) return;
    const frameId = window.requestAnimationFrame(() => {
      realignPanelToViewport();
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [expanded, realignPanelToViewport]);

  if (!currentRoom) return null;

  const anchoredStyle = position
    ? isDragging
      ? { left: position.x, top: position.y }
      : snapSide === 'right'
        ? { right: FLOAT_MARGIN, top: position.y }
        : { left: position.x, top: position.y }
    : { right: FLOAT_MARGIN, top: FLOAT_MARGIN };

  return (
    <>
      <div className="pointer-events-none fixed inset-0 z-[98] overflow-hidden">
        {bubbles.map((bubble) => {
          const delayMs = Math.max(0, bubble.createdAt - Date.now());
          return (
            <span
              key={bubble.id}
              className="absolute bottom-0 select-none"
              aria-hidden
              style={{
                left: `${bubble.leftPercent}%`,
                fontSize: `${bubble.sizePx}px`,
                animationName: 'emoji-reaction-float',
                animationDuration: `${bubble.durationMs}ms`,
                animationTimingFunction: 'cubic-bezier(0.2, 0.72, 0.33, 1)',
                animationDelay: `${delayMs}ms`,
                animationFillMode: 'forwards',
                opacity: 0,
                transform: 'translate(-50%, 0)',
              }}
            >
              {bubble.emoji}
            </span>
          );
        })}
      </div>
      <div
        ref={panelRef}
        className={`fixed z-[100] max-w-[calc(100vw-1rem)] ${
          isDragging ? '' : 'transition-[width,height,left,right,top] duration-200'
        } ${expanded ? 'w-[260px]' : 'w-14 h-14'}`}
        style={anchoredStyle}
      >
        {!expanded ? (
          <button
            type="button"
            onMouseDown={startDrag}
            onClick={() => {
              if (movedDuringDragRef.current) {
                movedDuringDragRef.current = false;
                return;
              }
              toggleExpanded(true);
            }}
            aria-label={t.ui.emojiTitle}
            className="h-14 w-14 rounded-full bg-yellow-100 border-2 border-yellow-300 shadow-xl flex items-center justify-center cursor-move select-none hover:bg-yellow-200"
          >
            <span className="text-2xl leading-none" aria-hidden>
              😊
            </span>
          </button>
        ) : (
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl shadow-xl overflow-hidden">
            <div
              className="px-3 py-2 bg-yellow-100 border-b border-yellow-200 flex items-center justify-between cursor-move select-none"
              onMouseDown={startDrag}
            >
              <button
                type="button"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={() => toggleExpanded(!expanded)}
                className="font-semibold text-yellow-900 text-sm flex items-center gap-2 hover:text-yellow-700"
              >
                <span aria-hidden>😊</span>
                <span>{t.ui.emojiTitle}</span>
              </button>
              <button
                type="button"
                onMouseDown={(event) => event.stopPropagation()}
                onClick={() => toggleExpanded(!expanded)}
                className="text-yellow-900/80 hover:text-yellow-900 text-xs font-semibold"
              >
                {t.ui.emojiPanelHide}
              </button>
            </div>
            <div className="p-3">
              <div className="grid grid-cols-5 gap-2">
                {REACTION_GRID_EMOJIS.map((emoji, index) => {
                  const isHighlighted = highlightedIndex === index;
                  return (
                    <button
                      key={`${emoji}-${index}`}
                      type="button"
                      className={`h-10 rounded-lg border text-xl transition active:scale-95 ${
                        isHighlighted
                          ? 'border-yellow-500 bg-yellow-100 shadow-[0_0_0_2px_rgba(234,179,8,0.45)]'
                          : 'border-yellow-300 bg-white hover:bg-yellow-100'
                      }`}
                      onClick={() => handleGridEmojiClick(emoji, index)}
                      aria-label={`${t.ui.emojiReactionAriaSendPrefix} ${emoji}`}
                    >
                      {emoji}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
      <style>{`
        @keyframes emoji-reaction-float {
          0% {
            transform: translate(-50%, 0) scale(0.8);
            opacity: 0;
          }
          14% {
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -55vh) scale(1.1);
            opacity: 0;
          }
        }
      `}</style>
    </>
  );
};
